import './CSVImport.css'

import * as XLSX from 'xlsx'

import { Button, Table } from 'react-bootstrap'
import React, { useState } from 'react'

import { axiosWithPostUrlEncoded as axios } from 'config/axios.js'
import { getDewarsAdd } from 'api/ispyb.js'
import qs from 'qs'
import { useSelector } from 'react-redux'
import { validateCell } from './CSVValidator'

const CAPACITY = 16

const DEWAR_TYPE = 'Dewar'

const CONTAINER_TYPE = 'Unipuck'

const NB_CSV_HEADER_LINES = 2

const NB_CSV_COLUMNS = 28

const XLS_LOCATION_COLUMN = 0
const XLS_PROTEIN_ACRONYM_COLUMN = 2
const XLS_SPG_COLUMN = 3
const XLS_NAME_COLUMN = 4
const XLS_PIN_BARCODE_COLUMN = 5
const XLS_EXPERIMENT_KIND_COLUMN = 9
const XLS_AIMED_RESOLUTION_COLUMN = 7
const XLS_REQUIRED_RESOLUTION_COLUMN = 8
const XLS_OBSERVED_RESOLUTION_COLUMN = 6
const XLS_COMMENTS_COLUMN = 19

const XLS_CELL_A_COLUMN = 11
const XLS_CELL_B_COLUMN = 12
const XLS_CELL_C_COLUMN = 13
const XLS_CELL_ALPHA_COLUMN = 14
const XLS_CELL_BETA_COLUMN = 15
const XLS_CELL_GAMMA_COLUMN = 16

export const DEWAR_CODE_COLUMN = 0
export const CONTAINER_CODE_COLUMN = 1
export const CONTAINER_TYPE_COLUMN = 2
export const LOCATION_COLUMN = 3
export const PROTEIN_ACRONYM_CSV_COLUMN = 4
export const NAME_COLUMN = 5
export const SPG_COLUMN = 7
export const CELL_A_COLUMN = 8
export const CELL_B_COLUMN = 9
export const CELL_C_COLUMN = 10
export const CELL_ALPHA_COLUMN = 11
export const CELL_BETA_COLUMN = 12
export const CELL_GAMMA_COLUMN = 13
export const EXPERIMENT_KIND_COLUMN = 14
export const AIMED_RESOLUTION_COLUMN = 15
export const REQUIRED_RESOLUTION_COLUMN = 16
export const PREFERRED_BEAM_DIAMETER_COLUMN = 17
export const NB_OF_POSITIONS_COLUMN = 18
export const AIMED_MULTIPLICITY_COLUMN = 19
export const AIMED_COMPLETENESS_COLUMN = 20
export const FORCED_SPG_COLUMN = 21
export const RADIATION_SENSITIVITY_COLUMN = 22
export const SMILES_COLUMN = 23
export const AXIS_RANGE_COLUMN = 24
export const MIN_OSC_WIDTH = 25
export const OBSERVED_RESOLUTION_COLUMN = 26
export const COMMENTS_COLUMN = 27

const CSVImport = ({ shippingId, proposalName, onReload }) => {
  const user = useSelector(state => state.user)
  const proteins = useSelector(state => state.proposalInfo.data.map(d => d.proteins).flat())

  let fileReader

  const [csv, setCsv] = useState([])
  const [csvValid, setCsvValid] = useState(false)
  const [message, setMessage] = useState('')

  function handleCsvRead() {
    const content = fileReader.result
      .split('\n')
      .slice(NB_CSV_HEADER_LINES)
      .map(line => line.split(','))
      .filter(line => line.length === NB_CSV_COLUMNS && line[PROTEIN_ACRONYM_CSV_COLUMN] && line[NAME_COLUMN])

    const newCsv = validatedCsv(content)
    const isValid = !newCsv.some(line => line.some(cell => cell.error))
    setCsv(newCsv)
    setCsvValid(newCsv.length && isValid)
  }

  const validatedCsv = content => {
    const acronyms = proteins.map(p => p.acronym)
    const names = content.map(line => `${line[DEWAR_CODE_COLUMN]}-${line[NAME_COLUMN]}`)
    const locations = content.map(line => `${line[CONTAINER_CODE_COLUMN]}-${line[LOCATION_COLUMN]}`)
    return content.map(line => line.map((cell, index) => validateCell(line, index, acronyms, names, locations)))
  }

  const handleFileChosen = file => {
    if (file.name.includes('xls')) {
      handleXLSChosen(file)
    }
    if (file.name.includes('csv')) {
      handleCSVChosen(file)
    }
  }

  const handleCSVChosen = file => {
    fileReader = new FileReader()
    fileReader.onloadend = handleCsvRead
    fileReader.readAsText(file)
  }

  function handleXlsRead() {
    const content = fileReader.result
    const wb = XLSX.read(content, { type: 'binary' })
    const xlsToData = wb.SheetNames.map(sheetName => XLSX.utils.sheet_to_csv(wb.Sheets[sheetName]))
    const readyForCsv = xlsToData
      .map(sheet => createCsvDataArray(sheet))
      .flat()
      .concat()
    const newXls = validatedCsv(readyForCsv)
    const isValid = !newXls.some(line => line.some(cell => cell.error))
    setCsv(newXls)
    setCsvValid(newXls.length && isValid)
  }

  const handleXLSChosen = file => {
    fileReader = new FileReader()
    fileReader.onloadend = handleXlsRead
    fileReader.readAsBinaryString(file)
  }

  const containerName = sheet =>
    sheet
      .split('\n')
      .slice(1, 2)
      .map(line => line.split(','))
      .flat()[3]

  const dewarName = sheet =>
    sheet
      .split('\n')
      .slice(2, 3)
      .map(line => line.split(','))
      .flat()[3]

  const cleanedData = sheet =>
    sheet
      .split('\n')
      .slice(7, 23)
      .map(line => line.split(','))

  function createCsvDataArray(sheet) {
    const parcelName = dewarName(sheet)
    const contName = containerName(sheet)
    const cleanedSheet = cleanedData(sheet)
    const filledSheet = cleanedSheet.map(line => [
      parcelName,
      contName,
      CONTAINER_TYPE,
      line[XLS_LOCATION_COLUMN] || '',
      line[XLS_PROTEIN_ACRONYM_COLUMN] || '',
      line[XLS_NAME_COLUMN] || '',
      line[XLS_PIN_BARCODE_COLUMN] || '',
      line[XLS_SPG_COLUMN] || '',
      line[XLS_CELL_A_COLUMN] || '',
      line[XLS_CELL_B_COLUMN] || '',
      line[XLS_CELL_C_COLUMN] || '',
      line[XLS_CELL_ALPHA_COLUMN] || '',
      line[XLS_CELL_BETA_COLUMN] || '',
      line[XLS_CELL_GAMMA_COLUMN] || '',
      line[XLS_EXPERIMENT_KIND_COLUMN] || '',
      line[XLS_AIMED_RESOLUTION_COLUMN] || '',
      line[XLS_REQUIRED_RESOLUTION_COLUMN] || '',
      '',
      '',
      '',
      '',
      '',
      '',
      '',
      '',
      '',
      line[XLS_OBSERVED_RESOLUTION_COLUMN] || '',
      line[XLS_COMMENTS_COLUMN] || '',
    ])
    return filledSheet
      .map(line => line.map(cell => cell.trim()))
      .filter(line => line.length === NB_CSV_COLUMNS && line[PROTEIN_ACRONYM_CSV_COLUMN] && line[NAME_COLUMN])
  }

  const createDewarFromCsvLine = line => {
    const protein = proteins.find(p => p.acronym === line[PROTEIN_ACRONYM_CSV_COLUMN])
    const proteinVO = {
      proteinId: protein?.proteinId || null,
      name: protein?.name || null,
      acronym: protein?.acronym || null,
      safetyLevel: protein?.safetyLevel || null,
      molecularMass: protein?.molecularMass || null,
      proteinType: protein?.proteinType || null,
      sequence: protein?.sequence || null,
      personId: protein?.personId || null,
      timeStamp: protein?.timeStamp || null,
      isCreatedBySampleSheet: protein?.isCreatedBySampleSheet || null,
      externalId: protein?.externalId || null,
    }

    const diffractionPlanVO = {
      radiationSensivity: line[RADIATION_SENSITIVITY_COLUMN] ? line[RADIATION_SENSITIVITY_COLUMN] : null,
      aimedCompleteness: line[AIMED_COMPLETENESS_COLUMN] ? line[AIMED_COMPLETENESS_COLUMN] : null,
      aimedMultiplicity: line[AIMED_MULTIPLICITY_COLUMN] ? line[AIMED_MULTIPLICITY_COLUMN] : null,
      aimedResolution: line[AIMED_RESOLUTION_COLUMN] ? line[AIMED_RESOLUTION_COLUMN] : null,
      requiredResolution: line[REQUIRED_RESOLUTION_COLUMN] ? line[REQUIRED_RESOLUTION_COLUMN] : null,
      forcedSpaceGroup: line[FORCED_SPG_COLUMN] ? line[FORCED_SPG_COLUMN] : null,
      experimentKind: line[EXPERIMENT_KIND_COLUMN] ? line[EXPERIMENT_KIND_COLUMN] : null,
      observedResolution: line[OBSERVED_RESOLUTION_COLUMN] ? line[OBSERVED_RESOLUTION_COLUMN] : null,
      preferredBeamDiameter: line[PREFERRED_BEAM_DIAMETER_COLUMN] ? line[PREFERRED_BEAM_DIAMETER_COLUMN] : null,
      numberOfPositions: line[NB_OF_POSITIONS_COLUMN] ? line[NB_OF_POSITIONS_COLUMN] : null,
      axisRange: line[AXIS_RANGE_COLUMN] ? line[AXIS_RANGE_COLUMN] : null,
      minOscWidth: line[MIN_OSC_WIDTH] ? line[MIN_OSC_WIDTH] : null,
    }

    const crystalVO = {
      spaceGroup: line[SPG_COLUMN] ? line[SPG_COLUMN] : null,
      cellA: line[CELL_A_COLUMN] ? line[CELL_A_COLUMN] : null,
      cellB: line[CELL_B_COLUMN] ? line[CELL_B_COLUMN] : null,
      cellC: line[CELL_C_COLUMN] ? line[CELL_C_COLUMN] : null,
      cellAlpha: line[CELL_ALPHA_COLUMN] ? line[CELL_ALPHA_COLUMN] : null,
      cellBeta: line[CELL_BETA_COLUMN] ? line[CELL_BETA_COLUMN] : null,
      cellGamma: line[CELL_GAMMA_COLUMN] ? line[CELL_GAMMA_COLUMN] : null,
      proteinVO: proteinVO,
    }

    const sampleVO = {
      name: line[NAME_COLUMN] ? line[NAME_COLUMN] : null,
      location: line[LOCATION_COLUMN] ? line[LOCATION_COLUMN] : null,
      diffractionPlanVO: diffractionPlanVO,
      crystalVO: crystalVO,
      smiles: line[SMILES_COLUMN] ? line[SMILES_COLUMN] : null,
      comments: line[COMMENTS_COLUMN] ? line[COMMENTS_COLUMN] : null,
    }

    const containerVO = {
      code: line[CONTAINER_CODE_COLUMN] ? line[CONTAINER_CODE_COLUMN] : null,
      containerType: line[CONTAINER_TYPE_COLUMN] ? line[CONTAINER_TYPE_COLUMN] : null,
      capacity: CAPACITY,
      sampleVOs: [sampleVO],
    }
    return {
      code: line[DEWAR_CODE_COLUMN] ? line[DEWAR_CODE_COLUMN] : null,
      type: DEWAR_TYPE,
      containerVOs: [containerVO],
    }
  }

  const handleSave = async event => {
    event.preventDefault()
    const dewarsByCode = csv
      .map(line => line.map(cell => cell.text.trim()))
      .map(line => createDewarFromCsvLine(line))
      .reduce((acc, dewar) => {
        const oldDewar = acc[dewar.code] || {}
        const oldContainers = oldDewar.containerVOs || []
        const oldContainer = oldContainers.find(c => c.code === dewar.containerVOs[0].code) || {}
        const oldSamples = oldContainer.sampleVOs || []
        const newSamples = oldSamples.concat(dewar.containerVOs[0].sampleVOs)
        const newContainer = { ...dewar.containerVOs[0], sampleVOs: newSamples }
        const newContainers = oldContainers.filter(c => c.code !== dewar.containerVOs[0].code).concat(newContainer)
        const newDewar = { ...dewar, containerVOs: newContainers }
        return { ...acc, [dewar.code]: newDewar }
      }, {})

    const saveData = {
      dewars: JSON.stringify(Object.values(dewarsByCode)),
    }

    await axios
      .post(getDewarsAdd(user, proposalName, shippingId), qs.stringify(saveData))
      .then(() => setMessage('Saved !'))
    setCsv([])
    setCsvValid(false)
    fileReader = new FileReader()
    onReload()
  }

  return (
    <>
      <div>
        <h2>Import from CSV or Excel</h2>
        <p />
        <input type="file" accept=".csv,.xls,.xlsx" onChange={e => handleFileChosen(e.target.files[0])} />
        <p />
        <div>
          <Table border="3">
            <thead>
              <tr>
                <th>Parcel Name</th>
                <th>Container Name</th>
                <th>Container Type</th>
                <th>#</th>
                <th>Protein Acronym</th>
                <th>Sample Name</th>
                <th>Pin Barcode</th>
                <th>Space group</th>
                <th>a</th>
                <th>b</th>
                <th>c</th>
                <th>α</th>
                <th>β</th>
                <th>γ</th>
                <th>Exp. Type</th>
                <th>Aimed Resolution</th>
                <th>Required Resolution</th>
                <th>Beam Diameter</th>
                <th>Number of position</th>
                <th>Aimed Multiplicity</th>
                <th>Aimed Completeness</th>
                <th>Forced SPG</th>
                <th>Radiation Sensitivity</th>
                <th>SMILES</th>
                <th>Tot Rot. Angle</th>
                <th>Min Osc Angle</th>
                <th>Observed Resolution</th>
                <th>Comments</th>
              </tr>
            </thead>
            <tbody>
              {csv.map((line, i) => (
                <tr key={i}>
                  {line.map((cell, j) => (
                    <td className={cell.error && 'error'} key={`${i}-${j}`}>
                      {cell.text}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
        <p />
        <div>
          <Button bsStyle="primary" disabled={!csvValid} onClick={handleSave}>
            Save
          </Button>
        </div>
        <div>
          <h3>{message}</h3>
        </div>
      </div>
    </>
  )
}

export default CSVImport
